import { useEffect, useRef, useState } from 'react';
import type { Dir } from '../wire';
import { hapticPulse } from './haptics';
import { DEFAULT_PARAMS, classifySampleOutcome, flushPendingSettle, initJoystickState, resolveDir, updateJoystick, type JoystickState } from './joystickModel';
import { isTraceEnabled, traceJoystickRepeat, traceJoystickSample } from './trace';

export interface JoystickProps {
  onDir: (dir: Dir) => void;
  disabled?: boolean;
}

// TURN-INTENT TRACE: capture-build only -- see main.tsx's TRACE_BUILD doc
// comment and game/trace.ts's module doc comment. Every call site below is
// gated `if (TRACE_BUILD && isTraceEnabled())` (not just `isTraceEnabled()`
// alone) so that in a plain/prod build (TRACE_BUILD folds to `false` at
// build time) the bundler can prove the branch dead and eliminate it,
// including the `traceJoystickRepeat`/`traceJoystickSample` imports -- a
// runtime-only `isTraceEnabled()` check alone would leave this code (and
// the classifier hot-path call) present in the bundle even though it never
// executes.
const TRACE_BUILD = import.meta.env.VITE_ENABLE_TRACE === '1';

/** The 4 real directions a joystick read can resolve to -- unlike `Dir`,
 * never 'none' (a neutral read is `null`, see resolveJoyDir below). Re-
 * exported from the pure model (joystickModel.ts) so there is one
 * definition, not a parallel copy that can drift. */
export type { JoyDir } from './joystickModel';
import type { JoyDir } from './joystickModel';

/** All of the tunables (deadzone, leash radius, axis-switch bias, hold-
 * repeat interval, origin mode, gate shape, knob visual-origin/gain/ratio)
 * now live in `joystickModel.ts`'s `DEFAULT_PARAMS` -- this component reads
 * them from there (never hardcodes its own copy) so "what we measure in
 * the joystick-lab" and "what ships" cannot drift apart. `repeatMs` is also
 * needed here for the DOM hold-repeat timer, so it's aliased locally; the
 * knob's rendered offset itself is now computed entirely inside
 * `updateJoystick` (see `result.knob` in `updateFromPoint` below) instead
 * of being re-derived here from a locally-duplicated ratio/scale. */
const { repeatMs: REPEAT_MS } = DEFAULT_PARAMS;

/**
 * Pure offset -> direction mapping, unit tested directly in
 * tests/unit/Joystick.test.tsx (extracted specifically so the deadzone +
 * dominant-axis + axis-bias math is testable without simulating real
 * pointer geometry / DOM measurement at all).
 *
 * `dx`/`dy` are the knob's offset from the base centre in CSS px (screen
 * space: +y is DOWN, matching pointer/client coordinates), `radius` is half
 * the square base's side in the same units. Returns `null` inside the
 * deadzone (no direction), otherwise the dominant-axis cardinal direction --
 * Pac is a 4-way grid, so a square gate + dominant-axis resolver maps 1:1
 * and kills diagonal ambiguity entirely (no diagonal reads are possible).
 *
 * `prevDir` is the direction currently considered active (or `null`) -- the
 * axis-switch bias is measured against it: once an axis (horizontal or
 * vertical) is committed, the OTHER axis's displacement must exceed the
 * committed axis's by `AXIS_BIAS` before the resolved direction switches
 * axes, so a knob resting near a 45° diagonal doesn't flip-flop on every
 * sub-pixel jitter of a real thumb. A sign flip along the SAME axis (e.g.
 * right -> left) is always immediate -- there is no bias against reversing
 * within one axis. Pass `null` for a fresh read (e.g. right after a
 * deadzone exit).
 *
 * This is now a thin wrapper over the parameterized `resolveDir` in
 * joystickModel.ts, called with `DEFAULT_PARAMS` -- kept as a named export
 * here (rather than importing `resolveDir` directly at call sites) purely
 * for test-file backwards-compatibility (tests/unit/Joystick.test.tsx
 * imports it by this name and must not change, see task Part 1).
 */
export function resolveJoyDir(dx: number, dy: number, radius: number, prevDir: JoyDir | null = null): JoyDir | null {
  return resolveDir(dx, dy, radius, prevDir, DEFAULT_PARAMS);
}

/** setPointerCapture/hasPointerCapture/releasePointerCapture are genuinely
 * unimplemented in jsdom (throws) and are best-effort even in real
 * browsers -- never let a capture call throw into a pointer handler. */
function trySetCapture(el: Element, pointerId: number): void {
  try {
    el.setPointerCapture(pointerId);
  } catch {
    // best-effort only -- pointermove still reaches this element via
    // normal bubbling as long as the pointer stays over it.
  }
}
function tryReleaseCapture(el: Element, pointerId: number): void {
  try {
    if (el.hasPointerCapture(pointerId)) el.releasePointerCapture(pointerId);
  } catch {
    // see trySetCapture
  }
}

/** Retro-arcade thumb-draggable virtual joystick: a fixed square-gated base
 * (pinned bottom-centre like the D-pad it replaces as the default touch
 * control, see GameScreen.tsx's `.joystick-dock`) + a knob the user drags.
 * Square gate + dominant-axis resolution maps 1:1 onto Pac's 4-way grid
 * movement and kills diagonal ambiguity. Pointer Events (not touch/mouse-
 * specific handlers) drive it so mouse, touch, and pen all work identically
 * -- same approach as DPad.tsx.
 *
 * One input per direction CHANGE, matching DPad.tsx / the engine's own
 * "keeps moving until a wall" behaviour (packages/engine/src/pacman.ts) --
 * `onDir` fires only when the resolved direction actually changes, not on
 * every pointermove tick. Releasing (pointerup/cancel/leave) recentres the
 * knob but deliberately does NOT send a 'none' direction, matching
 * DPad.tsx's "release stops sending, doesn't stop the Pac" contract.
 */
export function Joystick({ onDir, disabled }: JoystickProps): JSX.Element {
  const baseRef = useRef<HTMLDivElement | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  // Gesture state (origin-tracking + resolved direction + the hybrid-mode
  // commit flag) now lives entirely in joystickModel.ts's JoystickState --
  // this ref just carries it between DOM events across one continuous
  // drag. Reset to a fresh state on every pointerdown / release.
  const modelRef = useRef<JoystickState>(initJoystickState());
  const repeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // SETTLE WINDOW: a `setTimeout` armed for a pending direction-change
  // candidate's deadline (`pending.startT + settleMs`, see joystickModel.ts's
  // `PendingSettle`), so a window still resolves even if the thumb holds
  // still after a decisive flick and no further `pointermove` arrives to
  // naturally re-evaluate it. Re-armed on every `updateFromPoint` call while
  // a window is open, cleared the instant one isn't (resolved, cancelled by
  // a reverting sample, or the gesture ended).
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [knob, setKnob] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);

  function clearSettleTimer(): void {
    if (settleTimerRef.current !== null) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }

  /** The one place a resolved direction actually becomes an `onDir` call +
   * haptic + hold-repeat timer -- shared by the synchronous commit path
   * (`updateFromPoint`, when a change resolves on the pointer sample that
   * caused it) and the settle-window's delayed commit path
   * (`flushSettleTimer`, when a window's deadline fires with no further
   * pointer sample), so there is exactly one implementation of "what
   * happens when a direction commits", not two that could drift apart. */
  function commitDirection(dir: NonNullable<JoystickState['dir']>): void {
    stopRepeat();
    hapticPulse();
    onDir(dir);
    // keep asserting the held direction so a held stick stays live
    repeatRef.current = setInterval(() => {
      if (modelRef.current.dir) {
        // TRACE ORDERING: must be recorded BEFORE onDir()/sendInput()
        // below -- traceSent's back-linking (trace.ts) scans BACKWARD
        // for the nearest not-yet-linked EMITTED entry with a matching
        // dir at the moment it runs; logging this repeat's own entry
        // AFTER calling onDir would mean it doesn't exist in the ring
        // yet when traceSent looks for it, so the repeat's own seq
        // could never link onto its own entry (only onto some earlier,
        // already-stale one, or nothing).
        if (TRACE_BUILD && isTraceEnabled()) traceJoystickRepeat(modelRef.current.dir);
        onDir(modelRef.current.dir);
      }
    }, REPEAT_MS);
  }

  /** Fires when a settle window's deadline elapses with no intervening
   * pointer sample. Forces the window's best-so-far candidate to commit,
   * exactly as `flushPendingSettle` (joystickModel.ts) describes -- mirrors
   * `runGestureDetailed`'s offline "deadline before the next sample" flush
   * for the case where there IS no next sample yet. */
  function flushSettleTimer(): void {
    settleTimerRef.current = null;
    const state = modelRef.current;
    if (state.pending === null) return; // already resolved by a move in the meantime
    const flush = flushPendingSettle(state.dir, state.pending);
    modelRef.current = { ...state, dir: flush.dir, pending: null };
    if (flush.changed && flush.dir) commitDirection(flush.dir);
  }

  /** Arms (or re-arms) `settleTimerRef` for the CURRENT `modelRef.current`
   * settle state -- clears it when there's nothing pending. Called after
   * every `updateJoystick` step so the timer always reflects the freshest
   * `pending.startT`/deadline. */
  function syncSettleTimer(): void {
    const pending = modelRef.current.pending;
    if (pending === null) {
      clearSettleTimer();
      return;
    }
    clearSettleTimer();
    const remaining = Math.max(0, pending.startT + DEFAULT_PARAMS.settleMs - performance.now());
    settleTimerRef.current = setTimeout(flushSettleTimer, remaining);
  }

  function updateFromPoint(clientX: number, clientY: number): void {
    const base = baseRef.current;
    if (!base) return;
    const rect = base.getBoundingClientRect();
    const radius = rect.width / 2;
    const prevDir = modelRef.current.dir;
    // All origin-tracking (the trailing leash) + deadzone/dominant-axis/
    // axis-bias/settle-window resolution happens inside the model, driven
    // by DEFAULT_PARAMS -- see joystickModel.ts for the full rationale of
    // each tunable.
    const result = updateJoystick(modelRef.current, { x: clientX, y: clientY }, performance.now(), radius, DEFAULT_PARAMS);
    modelRef.current = result.state;
    syncSettleTimer();

    // Knob's rendered offset (already clamped to the square gate, per
    // DEFAULT_PARAMS.visualOrigin/knobGain/knobRatio) -- see
    // `computeKnob` in joystickModel.ts for the single-source-of-truth math.
    setKnob(result.knob);

    // TURN-INTENT TRACE: gated on isTraceEnabled() so this is a single
    // boolean check (no object allocation, no classifier call) on the hot
    // path when tracing is off -- see trace.ts's module doc comment.
    if (TRACE_BUILD && isTraceEnabled()) {
      const outcome = classifySampleOutcome(result.dx, result.dy, radius, prevDir, result.dir);
      traceJoystickSample(performance.now(), { x: clientX, y: clientY }, result.dx, result.dy, result.dir, outcome);
    }

    if (result.changed) {
      if (result.dir) {
        commitDirection(result.dir);
      } else {
        stopRepeat();
      }
    }
  }

  function stopRepeat(): void {
    if (repeatRef.current !== null) {
      clearInterval(repeatRef.current);
      repeatRef.current = null;
    }
  }

  function recenter(): void {
    stopRepeat();
    clearSettleTimer();
    modelRef.current = initJoystickState();
    setKnob({ x: 0, y: 0 });
    setDragging(false);
  }

  // Belt-and-suspenders: never leak the repeat/settle timers if unmounted
  // mid-drag or mid-settle-window.
  useEffect(() => () => {
    stopRepeat();
    clearSettleTimer();
  }, []);

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>): void {
    if (disabled) return;
    const base = baseRef.current;
    if (!base) return;
    pointerIdRef.current = e.pointerId;
    // Fresh gesture state -- the model seeds its own origin to this press
    // point on the first `updateJoystick` call below (state.origin is
    // null), so there is no need to pre-seed it here.
    modelRef.current = initJoystickState();
    trySetCapture(base, e.pointerId);
    setDragging(true);
    updateFromPoint(e.clientX, e.clientY);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>): void {
    // Gate on the SYNCHRONOUS pointerId ref, NOT the async `dragging` state:
    // `setDragging(true)` in pointerdown doesn't commit until React re-renders,
    // so a real touch flick's first move(s) arrive with `dragging` still false
    // and were being DROPPED -- the knob then lagged the thumb and it felt like
    // you had to shove the stick to the edge before it registered. The ref is
    // set synchronously in pointerdown, so the very first move now tracks.
    // Gate only on "a drag is active" (pointerIdRef set synchronously in
    // pointerdown), NOT on the async `dragging` state (which dropped the first
    // real-flick moves -- the "shove it to the edge" bug) and NOT on an exact
    // e.pointerId match (synthetic dispatched events, e.g. Playwright, don't
    // populate pointerId, and the pointer is already setPointerCapture'd to
    // this base so no stray pointer's moves reach it anyway).
    if (disabled || pointerIdRef.current === null) return;
    updateFromPoint(e.clientX, e.clientY);
  }

  /** CORRECTION to the previously-diagnosed "box exit freezes the joystick"
   * theory (was: also wired to `onPointerLeave`; see BUGLEDGER.md and
   * boundaryModel.ts for the full account, including the RED/GREEN numbers
   * from the TEST-ONLY model this is about): the earlier diagnosis assumed
   * "pointer capture does NOT redirect boundary/enter-leave events, they
   * still fire from the real hit-test position." That assumption is WRONG
   * for real, spec-compliant browsers, this codebase's own included --
   * empirically verified against the actual Chromium this project's e2e
   * suite drives (see tests/e2e/joystickBoundary.spec.ts): with a real
   * captured pointer (mouse OR touch) dragged far outside the captured
   * element's bounds, `pointerleave`/`pointerout`/`lostpointercapture` do
   * NOT fire at all while capture is held -- only `pointermove` keeps
   * firing, and it keeps firing correctly however far outside the element
   * the pointer goes. The boundary events all fire together, once,
   * retroactively, only at release. This matches MDN's documented behaviour
   * for `setPointerCapture`: "If a pointer event is captured, pointerover,
   * pointerenter, pointerout, and pointerleave events won't fire while the
   * pointer remains captured" -- boundary-event suppression during capture
   * is standard Pointer Events behaviour, not a Chromium quirk, so
   * `onPointerLeave` bound here could NEVER have fired for a successfully
   * captured pointer (and `trySetCapture` at L67 does succeed in every real
   * browser this ships to) -- meaning the diagnosed freeze does not
   * reproduce. It was a real, honestly-run simulation result on a TEST-ONLY
   * model (`boundaryModel.ts`'s `runGestureWithBoundary`) that turned out
   * not to correspond to the actual shipped DOM contract; see that file and
   * gateLab.test.ts's "GATE-SIZE STEP 5" for the corrected account.
   *
   * `onPointerLeave` is removed anyway, not reverted-in: it is a no-op in
   * every browser tested (proven above -- it never fires during a held
   * capture), it documented an incorrect mental model, and it is a
   * meaningless residual attachment now that its only plausible firing
   * window (capture failing to be set at all, see `trySetCapture`'s
   * best-effort `catch`) is a case where a stray `pointerleave` still isn't
   * the right signal to act on (a non-captured drag that leaves the element
   * simply stops receiving `pointermove` via normal bubbling -- there is
   * nothing new for `handlePointerEnd` to clean up that leaving didn't
   * already silently stop). The real terminators for a captured gesture are
   * `pointerup` (thumb lifted) and `pointercancel` (OS/browser aborted it).
   * `onLostPointerCapture` is added here too, deliberately: it is the
   * correct, spec-defined signal for "this element no longer has exclusive
   * delivery of this pointer's events" (capture programmatically released
   * elsewhere, too many active pointers, etc.) -- once that fires, further
   * `pointermove`s for this pointer are no longer guaranteed to reach this
   * element, so ending the gesture there is honest, not a mitigation, even
   * though I could not reproduce it firing spuriously either.
   *
   * What this change does NOT affect either way: a push beyond the gate was
   * already handled correctly by the existing square-gate clamp --
   * `resolveDir` never reads `radius` for anything but an early
   * `radius <= 0` bail-out (see joystickModel.ts), so an out-of-box sample
   * resolves a direction exactly like an in-box one (pin-at-the-edge-and-
   * keep-steering, like a physical stick hitting its limit), and the KNOB's
   * rendered position is separately clamped to the gate by
   * `computeKnob`/`clampAxis` -- neither mechanism ever cared about
   * `onPointerLeave`. */
  function handlePointerEnd(): void {
    const base = baseRef.current;
    if (base && pointerIdRef.current !== null) {
      tryReleaseCapture(base, pointerIdRef.current);
    }
    pointerIdRef.current = null;
    recenter();
  }

  return (
    <div
      ref={baseRef}
      className="joystick-base"
      data-testid="joystick"
      role="group"
      aria-label="Movement joystick"
      aria-disabled={disabled || undefined}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onLostPointerCapture={handlePointerEnd}
    >
      {/* TOP-10 #5 fix: the base previously read as a decorative ball
          resting in a socket, not a control with directional affordance --
          a static ring/track under the knob (`.joystick-track`, CSS below)
          plus 4 tiny cardinal chevrons hint "this drags in these
          directions" at rest, before the player's thumb ever touches it.
          Purely decorative (aria-hidden -- `role="group"`/`aria-label`
          above already carry the accessible semantics), static (no
          animation), and never intercepts pointer events (CSS
          `pointer-events: none`), so the existing drag geometry/tests are
          completely unaffected. */}
      <div className="joystick-track" aria-hidden="true" />
      <span className="joystick-hint joystick-hint--up" aria-hidden="true">
        ▲
      </span>
      <span className="joystick-hint joystick-hint--down" aria-hidden="true">
        ▼
      </span>
      <span className="joystick-hint joystick-hint--left" aria-hidden="true">
        ◀
      </span>
      <span className="joystick-hint joystick-hint--right" aria-hidden="true">
        ▶
      </span>
      <div
        className={`joystick-knob${dragging ? ' joystick-dragging' : ''}`}
        style={{ transform: `translate(${knob.x}px, ${knob.y}px)` }}
        aria-hidden="true"
      />
    </div>
  );
}
